/** Minimal HTTP layer for the platform API.
 *
 * The browser always talks to the API same-origin under `/api`: in dev Vite
 * proxies it to VITE_API_BASE_URL, and in production nginx proxies it behind the
 * ingress. So callers pass a service-relative path (e.g. `/platform/blocks`) and
 * this prepends `/api`. The OIDC access token is attached as a bearer when there
 * is a session; the local dev backend accepts requests without one. */

const API_PREFIX = "/api";

/** Thrown for any non-2xx response. `status` lets callers branch on 403, etc.;
    `code` carries a server-provided machine-readable reason when present (e.g.
    `user_not_found`). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiRequest {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** JSON body; serialised automatically. */
  body?: unknown;
  /** Bearer token, or null/undefined to send unauthenticated. */
  token?: string | null;
  signal?: AbortSignal;
  /** Passed straight to `fetch`. Diagnostics use `no-store` so an intermediary
      cannot serve a stale answer as the current one. */
  cache?: RequestCache;
}

/** Fetch `path` under `/api`, returning the parsed JSON body (or `undefined`
    for empty/204 responses). Throws {@link ApiError} on failure. */
export async function apiFetch<T>(
  path: string,
  { method = "GET", body, token, signal, cache }: ApiRequest = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_PREFIX}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
    ...(cache ? { cache } : {}),
  });

  if (!res.ok) {
    // Prefer a server-provided message, but never surface a raw error body.
    let message = res.statusText || `HTTP ${res.status}`;
    let code: string | undefined;
    try {
      const data = (await res.clone().json()) as {
        detail?: string;
        message?: string;
        code?: string;
      };
      message = data.detail ?? data.message ?? message;
      code = data.code;
    } catch {
      // non-JSON error body; keep the status text
    }
    throw new ApiError(res.status, message, code);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
